import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "rounded-none border-[var(--trio-border)] bg-[var(--trio-bg)]";

function move(arr, i, dir) {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

const ItemRow = ({ item, onChange, onRemove, onMove, testid }) => (
  <div data-testid={testid} className="grid grid-cols-12 gap-2 items-start">
    <Input value={item.name} onChange={(e) => onChange({ ...item, name: e.target.value })} placeholder="Item name" data-testid={`${testid}-name`} className={`${inputCls} col-span-12 sm:col-span-4`} />
    <Input value={item.desc || ""} onChange={(e) => onChange({ ...item, desc: e.target.value })} placeholder="Description (optional)" data-testid={`${testid}-desc`} className={`${inputCls} col-span-8 sm:col-span-5`} />
    <Input value={item.price || ""} onChange={(e) => onChange({ ...item, price: e.target.value })} placeholder="$12" data-testid={`${testid}-price`} className={`${inputCls} col-span-2 sm:col-span-1`} />
    <div className="col-span-2 flex gap-0.5 justify-end">
      <button type="button" onClick={() => onMove(-1)} className="p-2 hover:text-[var(--trio-primary)]" title="Move up"><ChevronUp className="w-4 h-4" /></button>
      <button type="button" onClick={() => onMove(1)} className="p-2 hover:text-[var(--trio-primary)]" title="Move down"><ChevronDown className="w-4 h-4" /></button>
      <button type="button" onClick={onRemove} data-testid={`${testid}-remove`} className="p-2 text-red-700" title="Remove"><Trash2 className="w-4 h-4" /></button>
    </div>
  </div>
);

const SectionBlock = ({ section, onChange, onRemove, onMove, testid }) => {
  const setItems = (items) => onChange({ ...section, items });
  return (
    <div data-testid={testid} className="border border-[var(--trio-border)] bg-[var(--trio-bg)] p-4 space-y-3">
      <div className="flex gap-2 items-center">
        <Input value={section.name} onChange={(e) => onChange({ ...section, name: e.target.value })} placeholder="Section name (e.g. Plates, Hookahs, Mimosas)" data-testid={`${testid}-name`} className={`${inputCls} font-bold uppercase`} />
        <button type="button" onClick={() => onMove(-1)} className="p-2" title="Move section up"><ChevronUp className="w-4 h-4" /></button>
        <button type="button" onClick={() => onMove(1)} className="p-2" title="Move section down"><ChevronDown className="w-4 h-4" /></button>
        <button type="button" onClick={onRemove} data-testid={`${testid}-remove`} className="p-2 text-red-700" title="Delete section"><Trash2 className="w-4 h-4" /></button>
      </div>
      {section.items.map((it, i) => (
        <ItemRow
          key={i}
          item={it}
          testid={`${testid}-item-${i}`}
          onChange={(v) => setItems(section.items.map((x, k) => (k === i ? v : x)))}
          onRemove={() => setItems(section.items.filter((_, k) => k !== i))}
          onMove={(d) => setItems(move(section.items, i, d))}
        />
      ))}
      <button type="button" onClick={() => setItems([...section.items, { name: "", desc: "", price: "" }])} data-testid={`${testid}-add-item`} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-[var(--trio-primary)]">
        <Plus className="w-3.5 h-3.5" /> Add item
      </button>
    </div>
  );
};

export const MenuEditor = ({ menuId, noteLabel, notePlaceholder, renderExtra }) => {
  const [menu, setMenu] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMenu(null);
    adminApi.get(`/admin/menus/${menuId}`).then(({ data }) => setMenu(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [menuId]);

  if (!menu) return <div className="text-sm text-[var(--trio-muted)]">Loading…</div>;

  const setSections = (sections) => setMenu({ ...menu, sections });

  async function save() {
    setSaving(true);
    try {
      const sections = menu.sections
        .map((s) => ({ ...s, items: s.items.filter((it) => it.name.trim()) }))
        .filter((s) => s.name.trim());
      const { id, updated_at, ...rest } = menu;
      const { data } = await adminApi.put(`/admin/menus/${menuId}`, { ...rest, sections });
      setMenu(data);
      toast.success("Menu saved — live now");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid={`menu-editor-${menuId}`} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs tracking-widest uppercase">Menu title</Label>
          <Input value={menu.title} onChange={(e) => setMenu({ ...menu, title: e.target.value })} data-testid={`menu-editor-${menuId}-title`} className={`mt-2 ${inputCls}`} />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase">{noteLabel}</Label>
          <Input value={menu.note || ""} onChange={(e) => setMenu({ ...menu, note: e.target.value })} placeholder={notePlaceholder} data-testid={`menu-editor-${menuId}-note`} className={`mt-2 ${inputCls}`} />
        </div>
      </div>
      {renderExtra && renderExtra(menu, setMenu)}
      {menu.sections.map((s, i) => (
        <SectionBlock
          key={i}
          section={s}
          testid={`menu-editor-${menuId}-section-${i}`}
          onChange={(v) => setSections(menu.sections.map((x, k) => (k === i ? v : x)))}
          onRemove={() => window.confirm(`Delete section "${s.name || "untitled"}"?`) && setSections(menu.sections.filter((_, k) => k !== i))}
          onMove={(d) => setSections(move(menu.sections, i, d))}
        />
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => setSections([...menu.sections, { name: "", items: [{ name: "", desc: "", price: "" }] }])} data-testid={`menu-editor-${menuId}-add-section`} className="inline-flex items-center gap-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)] px-4 py-2 text-xs uppercase tracking-widest font-bold">
          <Plus className="w-3.5 h-3.5" /> Add section
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid={`menu-editor-${menuId}-save`} className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs uppercase tracking-widest font-bold disabled:opacity-60">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save menu
        </button>
      </div>
    </div>
  );
};
